import React, { useState, useEffect, useContext } from "react"
import styled, { css } from "styled-components"
import { Link } from "gatsby"
import Img from "gatsby-image"
import Select from "react-select"
import { useInView } from "react-intersection-observer"
import { motion } from "framer-motion"

import theme from "./../../styles/theme"

import { InlineVideo, EmbedVideo } from "./../Shared"
import { generateSlug, generatePath } from "./../../utils/helpers"
import { DictionaryContext } from "./../../contexts/dictionary"

import Item from "./Item"
import Title from "./../Title"

const StyledSection = styled.section`
    padding: 8rem 0 1.5rem 0;

    ${props => props.theme.above.desktop`
        padding: 20rem 0 6rem 0;
    `}
`

const StyledTitle = styled(Title)`
    margin-bottom: 3rem;
`

const Filter = styled.div`
    position: relative;

    width: 100%;
    max-width: 80rem;

    margin: 0 auto 3rem auto;
    padding: 0 3rem;

    ${props => props.theme.above.desktop`
        grid-gap: 3rem;

        max-width: 45rem;
        margin: 0 auto 12rem auto;
        padding: 0;
    `}
`

const FilterInner = styled(motion.div)`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 1.5rem;

    position: relative;

    width: 100%;

    ${props => props.theme.above.desktop`
        grid-gap: 3rem;
    `}
`

const StyledSelect = styled(Select)`
    width: 100%;
`

const ListWrapper = styled.div`
    position: relative;

    width: 100%;

    padding: 0 1.5rem;

    ${props => props.theme.above.desktop`
        max-width: ${props => props.theme.breakpoints.extraWide}px;
        
        margin: 0 auto;
        padding: 0 6rem;
    `}
`

const ListNavigation = styled.div`
    position: relative;

    width: 100%;

    overflow: hidden;

    margin-bottom: 3.5rem;

    text-align: center;

    ${props => props.theme.above.desktop`
        display: flex;
        flex-direction: row;
        justify-content: flex-end;

        margin-bottom: 6rem;

        text-align: right;
    `}
`

const NavigationInner = styled(motion.div)`
    width: 100%;
    max-width: 40rem;

    ${props => props.theme.below.desktop`
        margin: 0 auto;
    `}
`

const NavigationStyles = css`
    display: inline-block;

    position: relative;

    font-size: 1.2rem;
    line-height: 2rem;
    letter-spacing: 0.12rem;
`

const NavigationText = styled.p`
    ${NavigationStyles}
`

const NavigationLink = styled(Link)`
    ${NavigationStyles}
`

const StyledList = styled(motion.ul)`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 2rem 1.5rem;

    width: 100%;

    margin-bottom: 3rem;

    ${props => props.theme.above.desktop`
        grid-template-columns: repeat(4, 1fr);
        grid-gap: 4rem 3rem;

        margin-bottom: 10rem;
    `}

    ${props => props.theme.above.wide`
        grid-gap: 7rem 6rem;
    `}
`

const MediaWrapper = styled(motion.li)`
    position: relative;

    height: 60vw;
    max-height: 35rem;

    background-color: ${props => props.theme.colors.darkLight};

    ${props =>
        props.index == 0 &&
        `
        grid-column-start: 1;
        grid-column-end: -1;
        
        grid-row-start: 4;
    `}

    ${props =>
        props.index == 1 &&
        `
        grid-column-start: 1;
        grid-column-end: -1;
        
        grid-row-start: 8;
    `}

    ${props =>
        props.index == 2 &&
        `
        grid-column-start: 1;
        grid-column-end: -1;
        
        grid-row-start: 12;
    `}

    ${props =>
        props.index == 3 &&
        `
        grid-column-start: 1;
        grid-column-end: -1;
        
        grid-row-start: 16;
    `}

    ${props => props.theme.above.desktop`
        height: 30vw;
        max-height: 56rem;

        margin-bottom: 0;

        ${props.index == 0 &&
            `
            grid-column-start: 3;
            grid-column-end: -1;
            
            grid-row-start: 2;
        `}

        ${props.index == 1 &&
            `
            grid-column-start: 1;
            grid-column-end: 3;
            
            grid-row-start: 4;
        `}

        ${props.index == 2 &&
            `
            grid-column-start: 3;
            grid-column-end: -1;
            
            grid-row-start: 6;
        `}

        ${props.index == 3 &&
            `
            grid-column-start: 1;
            grid-column-end: 3;
            
            grid-row-start: 8;
        `}
    `}
`

const StyledVideo = styled(EmbedVideo)``

const StyledImage = styled(Img)`
    width: 100%;
    height: 100%;
`

const NoResults = styled.div`
    padding: 0 6rem 2rem 6rem;

    text-align: center;

    ${props => props.theme.above.desktop`
        padding: 0 0 2rem 0;
    `}
`

const customStyles = {
    control: provided => ({
        ...provided,
        backgroundColor: theme.colors.dark,
        borderRadius: "none",
        borderColor: "#4a4a4a",
        minHeight: "auto",
        boxShadow: "none",
        cursor: "pointer",
        "&:hover": {
            color: theme.colors.white,
            background: theme.colors.darkLight,
        },
    }),
    indicatorsContainer: provided => ({
        ...provided,
        padding: "1rem",
    }),
    clearIndicator: () => ({
        padding: "0.5rem",
        opacity: 1,
    }),
    indicatorSeparator: () => ({
        display: "none",
    }),
    dropdownIndicator: () => ({
        padding: "0.5rem",
        opacity: 1,
    }),
    valueContainer: provided => ({
        ...provided,
        padding: "1.5rem 2rem",
    }),
    placeholder: provided => ({
        ...provided,
        color: theme.colors.white,
        fontFamily: theme.fontFamily.circularBook,
        fontSize: "1.4rem",
        lineHeight: "2rem",
    }),
    input: provided => ({
        ...provided,
        color: theme.colors.white,
        fontFamily: theme.fontFamily.circularBook,
        fontSize: "1.4rem",
        lineHeight: "2rem",
    }),
    singleValue: (provided, state) => ({
        ...provided,
        color: theme.colors.white,
        fontFamily: theme.fontFamily.circularBook,
        fontSize: "1.4rem",
        lineHeight: "2rem",
    }),
    menu: provided => ({
        ...provided,
        marginTop: 0,
        borderRadius: 0,
        backgroundColor: theme.colors.white,
    }),
    option: (provided, state) => ({
        ...provided,
        color: state.isSelected ? theme.colors.dark : theme.colors.darkText,
        backgroundColor: state.isFocused ? "#efefef" : "transparent",
        fontFamily: theme.fontFamily.circularBook,
        fontSize: "1.4rem",
        lineHeight: "2rem",
        "&:active": {
            color: theme.colors.dark,
            background: "#efefef",
        },
    }),
}

const List = ({
    lang,
    items,
    type,
    media,
    title,
    filters,
    description,
    navigation,
}) => {
    const dictionary = useContext(DictionaryContext)

    const [allItems, setAllItems] = useState(items)
    const [options, setOptions] = useState(filters)
    const [currentSize, setCurrentSize] = useState(null)
    const [currentColor, setCurrentColor] = useState(null)

    const [mediaItems, setMediaItems] = useState(media[0].items)
    const [randomMedia, setRandomMedia] = useState(null)

    // Filter options
    useEffect(() => {
        let newOptions = []

        filters.map((filter, a) => {
            filter.options.map((option, b) => {
                option.value = option[filter.type].slug
                option.label = option[filter.type].document.data.title.text
            })

            let filterType = {
                [filter.type]: filter.options,
            }

            newOptions.push(filterType)
        })

        setOptions(newOptions)
    }, [])

    // Filter switch
    useEffect(() => {
        let results

        if ((currentColor == null) & (currentSize == null)) {
            results = items
        } else if (currentColor != null && currentSize == null) {
            results = items.filter(item => {
                if (!item.data.body[1].primary.color.document) {
                    return false
                } else {
                    return (
                        item.data.body[1].primary.color.document.data.title
                            .text == currentColor.label
                    )
                }
            })
        } else if (currentColor == null && currentSize != null) {
            results = items.filter(item => {
                if (!item.data.product_size.document) {
                    return false
                } else {
                    return (
                        item.data.product_size.document.data.title.text ==
                        currentSize.label
                    )
                }
            })
        } else if (currentColor != null && currentSize != null) {
            results = items.filter(item => {
                if (!item.data.body[1].primary.color.document) {
                    return false
                } else if (
                    item.data.product_size.document.data.title.text ==
                        currentSize.label &&
                    item.data.body[1].primary.color.document.data.title.text ==
                        currentColor.label
                ) {
                    return true
                }
            })
        }

        setAllItems(results)
    }, [currentColor, currentSize])

    const setCurrentValue = (type, e) => {
        if (type == "color") {
            setCurrentColor(e)
        } else {
            setCurrentSize(e)
        }
    }

    const [ref, inView] = useInView({
        triggerOnce: true,
    })

    useEffect(() => {
        setRandomMedia(
            mediaItems[Math.floor(Math.random() * media[0].items.length)]
        )
    }, [])

    return (
        <StyledSection>
            <StyledTitle
                title={title.text}
                description={description.raw}
                alignment="center"
            />
            <Filter ref={ref}>
                <FilterInner
                    animate={inView ? "after" : "before"}
                    initial="before"
                    variants={{
                        before: {
                            opacity: 0,
                        },
                        after: {
                            opacity: 1,
                        },
                    }}
                    transition={{
                        duration: 1,
                        damping: 100,
                        stiffness: 100,
                        ease: "circOut",
                    }}
                >
                    {filters.map((item, i) => {
                        return (
                            <StyledSelect
                                key={i}
                                styles={customStyles}
                                theme={theme => ({
                                    ...theme,
                                    colors: {
                                        ...theme.colors,
                                        primary25: "#fff",
                                        primary: "#fff",
                                    },
                                })}
                                onChange={e => setCurrentValue(item.type, e)}
                                options={item.options}
                                value={
                                    item.type == "size"
                                        ? currentSize
                                        : currentColor
                                }
                                isClearable={true}
                                isSearchable={false}
                                placeholder={item.placeholder}
                            />
                        )
                    })}
                </FilterInner>
            </Filter>
            {allItems.length > 0 ? (
                <ListWrapper>
                    <StyledList
                        animate={inView ? "after" : "before"}
                        initial="before"
                        variants={{
                            before: {
                                opacity: 0,
                                y: 25,
                            },
                            after: {
                                opacity: 1,
                                y: 0,
                            },
                        }}
                        transition={{
                            duration: 0.55,
                            damping: 100,
                            stiffness: 100,
                            ease: "circOut",
                        }}
                    >
                        {allItems.map(({ data, uid }, index) => {
                            return (
                                <Item
                                    key={index}
                                    lang={lang}
                                    data={data}
                                    uid={uid}
                                />
                            )
                        })}
                        {randomMedia &&
                            allItems.length == items.length &&
                            allItems.length > 8 &&
                            media.map((item, index) => {
                                return (
                                    <MediaWrapper key={index} index={index}>
                                        <EmbedVideo
                                            lang={lang}
                                            data={
                                                randomMedia.video.document.data
                                            }
                                            product={
                                                randomMedia.product_link &&
                                                randomMedia.product_link
                                                    .document
                                            }
                                        />
                                    </MediaWrapper>
                                )
                                // switch (item.__typename) {
                                //     case 'PrismicCollectionPageCollectionMediaCollectionVideo': {
                                //         return (
                                //             <MediaWrapper
                                //                 key={index}
                                //                 index={index}
                                //             >
                                //                 <StyledVideo
                                //                     lang={lang}
                                //                     data={item.primary.video.document.data.inline_video}
                                //                     placeholder={item.primary.video.document.data.placeholder}
                                //                     product={item.primary.video.document.data.product_link && item.primary.video.document.data.product_link.document}
                                //                 />
                                //             </MediaWrapper>
                                //         )
                                //     }
                                //     case 'PrismicCollectionPageCollectionMediaCollectionImage': return (
                                //         <MediaWrapper
                                //             key={index}
                                //             index={index}
                                //             size={item.primary.size}
                                //         >
                                //             <StyledImage
                                //                 fluid={item.primary.image.fluid}
                                //                 alt={item.primary.image.alt}
                                //                 objectFit='cover'
                                //             />
                                //         </MediaWrapper>
                                //     )
                                // }
                            })}
                    </StyledList>
                    <ListNavigation>
                        <NavigationInner
                            animate={inView ? "after" : "before"}
                            initial="before"
                            variants={{
                                before: {
                                    opacity: 0,
                                    y: 25,
                                },
                                after: {
                                    opacity: 1,
                                    y: 0,
                                },
                            }}
                            transition={{
                                delay: 0.25,
                                duration: 0.55,
                                damping: 100,
                                stiffness: 100,
                                ease: "circOut",
                            }}
                        >
                            <NavigationText>{navigation.text}</NavigationText>
                            <NavigationLink
                                to={generatePath(
                                    lang,
                                    type == "classics"
                                        ? "collection"
                                        : "classics"
                                )}
                            >
                                {dictionary.check_the_living_creations}
                                {type == "classics"
                                    ? " Collection"
                                    : " Classics"}
                                .
                            </NavigationLink>
                        </NavigationInner>
                    </ListNavigation>
                </ListWrapper>
            ) : (
                <NoResults>
                    <h5>{dictionary.no_products}</h5>
                </NoResults>
            )}
        </StyledSection>
    )
}

export default List
